/**
 * Dispatch de encoders com cache e detecção de tipo.
 *
 * getEncoder(type)  → lazy load do encoder (1x por tipo) + cache
 * detectType(bytes) → detecta image/png|jpeg|webp pelos magic bytes
 *
 * Esta é a única camada que conhece qual encoder é de qual tipo.
 * Workers e UI chamam getEncoder() e recebem {encode} sem se preocupar.
 */

import * as png from './png.js';
import * as jpeg from './jpeg.js';
import * as webp from './webp.js';

const cache = new Map(); // type → {encode}

const PNG_SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIG = [0xff, 0xd8, 0xff];
const WEBP_RIFF = [0x52, 0x49, 0x46, 0x46]; // "RIFF"
const WEBP_NAME = [0x57, 0x45, 0x42, 0x50]; // "WEBP"

/**
 * Retorna o encoder para o tipo MIME. Cache hit/miss logged uma vez.
 * @param {'image/png' | 'image/jpeg' | 'image/webp'} type
 * @returns {Promise<{encode: Function}>}
 */
export async function getEncoder(type) {
  if (cache.has(type)) {
    return cache.get(type);
  }
  let mod;
  if (type === 'image/png') mod = png;
  else if (type === 'image/jpeg') mod = jpeg;
  else if (type === 'image/webp') mod = webp;
  else throw new Error(`Tipo não suportado: ${type}`);

  cache.set(type, mod);
  return mod;
}

/** Limpa o cache (útil pra tests). */
export function clearCache() {
  cache.clear();
}

/**
 * Detecta o tipo de imagem a partir dos primeiros bytes.
 * @param {Uint8Array} bytes - mínimo 12 bytes
 * @returns {'image/png' | 'image/jpeg' | 'image/webp' | null}
 */
export function detectType(bytes) {
  if (!bytes || bytes.length < 4) return null;
  if (matchesPrefix(bytes, PNG_SIG)) return 'image/png';
  if (matchesPrefix(bytes, JPEG_SIG)) return 'image/jpeg';
  if (matchesPrefix(bytes, WEBP_RIFF) && bytes.length >= 12 && matchesAt(bytes, 8, WEBP_NAME)) {
    return 'image/webp';
  }
  return null;
}

function matchesPrefix(bytes, sig) {
  if (bytes.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[i] !== sig[i]) return false;
  }
  return true;
}

function matchesAt(bytes, offset, sig) {
  if (bytes.length < offset + sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[offset + i] !== sig[i]) return false;
  }
  return true;
}
